import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { CustomTab, Loader } from "../../../Components/Other";
import { PrimaryButton } from "../../../Components/Cta";
import { APIService } from "../../../Service";

const RETURN_RANGE_ERROR = "Value must be greater than 0 and less than 50";

const mapReturnConfiguration = (returnConfiguration = []) =>
  returnConfiguration.map((item) => ({
    id: item.tenure_slab_id,
    duration: item.tenure_label,
    return: item.return_percentage ?? "",
  }));

export default function MapGoalsToTenure() {
  const [goalTabs, setGoalTabs] = useState([]);
  const [selectedGoalStyleId, setSelectedGoalStyleId] = useState(null);
  const [tenureReturns, setTenureReturns] = useState([]);
  const [returnErrors, setReturnErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    _getTenureReturnFilter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _getTenureReturnFilter = (goalStyleId) => {
    setLoading(true);
    const is_post = true;
    const payload = { goal_style_id: goalStyleId };

    APIService(is_post, "goal/tenure-return/filter", payload)
      .then((response) => {
        if (response.status_code === 200) {
          const { goals, selected_goal_style_id, return_configuration } =
            response.data;

          setGoalTabs(
            goals.map((goal) => ({
              id: goal.goal_style_id,
              label: goal.name,
              name: goal.name,
            })),
          );

          setSelectedGoalStyleId(selected_goal_style_id);
          setTenureReturns(mapReturnConfiguration(return_configuration));
          setReturnErrors({});
        } else {
          console.error(response);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const _handleTabChange = (goalStyleId) => {
    if (goalStyleId === selectedGoalStyleId) return;
    _getTenureReturnFilter(goalStyleId);
  };

  const _validateReturns = () => {
    const errors = {};

    tenureReturns.forEach((item) => {
      const value = item.return;
      const num = Number(value);

      if (value === "" || value === null || Number.isNaN(num)) {
        errors[item.id] = RETURN_RANGE_ERROR;
      } else if (num <= 0 || num >= 50) {
        errors[item.id] = RETURN_RANGE_ERROR;
      }
    });

    return errors;
  };

  const _handleReturnChange = (id, value) => {
    setTenureReturns((prev) =>
      prev.map((item) => (item.id === id ? { ...item, return: value } : item)),
    );

    if (returnErrors[id]) {
      setReturnErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const _handleSaveReturns = () => {
    const errors = _validateReturns();

    if (Object.keys(errors).length > 0) {
      setReturnErrors(errors);
      return;
    }

    setReturnErrors({});
    setSaving(true);

    const is_post = true;
    const payload = {
      goal_style_id: selectedGoalStyleId,
      returns: tenureReturns.map((item) => ({
        tenure_slab_id: item.id,
        return_percentage: Number(item.return),
      })),
    };

    APIService(is_post, "goal/tenure-return/edit", payload)
      .then((response) => {
        if (response.status_code === 200) {
          toast.success(response.message);
          setTenureReturns(
            mapReturnConfiguration(response.data.return_configuration),
          );
        } else {
          toast.error(response.message);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="row">
      <div className="col-lg-7 col-md-10 col-12">
        <div className="e-border-1px-pale-lavender e-border-radius-16 p-3 mt-3 e-bg-ghost-white-dark">
          <div className="row">
            <div className="col-12">
              <h3 className=" e-primary-font-poppins e-font-weight-600 e-font-16 line-height-24px">
                Goal category
              </h3>
            </div>

            <div className="col-12">
              <div className="e-tenure-tab overflow-x-auto  d-flex flex-nowrap column-gap-2 justify-content-md-start justify-content-between e-border-1px-bottom-magnolia-light e-pt-36px">
                {goalTabs.map((data, index) => (
                  <CustomTab
                    key={index}
                    type={1}
                    borderRadius="border-radius-4"
                    tabData={data}
                    tabIndex={selectedGoalStyleId}
                    background="bg-white"
                    handleChange={() => _handleTabChange(data.id)}
                  />
                ))}
              </div>
            </div>

            <div className="col-12 mt-md-4 mt-3">
              <h3 className=" e-primary-font-poppins e-font-weight-600 e-font-16 line-height-24px">
                Return configuration
              </h3>
            </div>

            <div className="col-lg-10 col-12">
              {loading ? (
                <div
                  style={{ height: "360px" }}
                  className="d-flex justify-content-center py-4"
                >
                  <Loader width={30} />
                </div>
              ) : (
                <div className="e-map-goals-tenure-table">
                  <div className="e-map-goals-tenure-table__header">
                    <span>Investment duration</span>

                    <span>Expected return (% p.a)</span>
                  </div>

                  {tenureReturns.map((item) => (
                    <div
                      className="e-map-goals-tenure-table__row"
                      key={item.id}
                    >
                      <span className="e-map-goals-tenure-table__duration">
                        {item.duration}
                      </span>

                      <div className="e-map-goals-tenure-table__input-wrapper">
                        <div className="e-map-goals-tenure-table__input-field">
                          <input
                            type="number"
                            className={`e-map-goals-tenure-table__input ${
                              returnErrors[item.id] ? "e-border-1px-ruby" : ""
                            }`}
                            value={item.return}
                            onChange={(e) =>
                              _handleReturnChange(item.id, e.target.value)
                            }
                          />

                          <span className="e-map-goals-tenure-table__suffix">
                            %
                          </span>
                        </div>

                        <span
                          className={`e-map-goals-tenure-table__error e-text-ruby ${
                            returnErrors[item.id]
                              ? "e-map-goals-tenure-table__error--visible"
                              : ""
                          }`}
                        >
                          {returnErrors[item.id] || ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-12">
              <div className="d-flex justify-content-end pt-3">
                <PrimaryButton
                  ClassName="e-border-radius-pill e-font-weight-500 py-2 e-font-14 e-bg-blue-violet text-white e-primary-font-poppins"
                  name={"Save configured returns"}
                  loading={saving}
                  onPress={_handleSaveReturns}
                  disabled={saving || loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
